/**
 * @fileoverview Canonical department-logo resolution shared across the web apps.
 * @module reporting/branding
 *
 * Responsibilities: - Resolve a department's effective logo URL from the
 *                     converged branding shape, preferring the uploaded
 *                     `branding.logo` ref over the legacy `branding.logoUrl`
 *                     string, with the same precedence for the second slot.
 * Dependencies:     - none (pure data).
 * Architecture:     - Single source of truth for the web side (tenant-web
 *                     useBranding + document builder thumbnails). The report
 *                     renderer (`api/packages/document-render`) applies the
 *                     identical precedence on the server/worker side; the rule
 *                     is intentionally a trivial coalesce so the two stay in
 *                     lockstep without a runtime dependency across the api/web
 *                     package boundary.
 * Notes:            - `blobUrl` may be a blob name that a downstream resolver
 *                     turns into a renderable URL; this helper only chooses
 *                     which field wins, it does not fetch or sign anything.
 */

/**
 * @contract BrandingLogoRefLike
 * Purpose: Structural shape of an uploaded department logo reference.
 * Fields:  uploadedFileId — source uploaded-file id; blobUrl — blob name / URL.
 * Rules:   `blobUrl` is the only field consulted when resolving a render URL.
 * Sync:    local-first; mirrors `BrandingLogoRef` in system-api tenant types.
 */
export interface BrandingLogoRefLike {
  uploadedFileId?: string;
  blobUrl?: string;
  fileName?: string;
  mimeType?: string;
  width?: number;
  height?: number;
}

/**
 * @contract DepartmentBrandingLike
 * Purpose: Structural shape of the branding block consumed when resolving logos.
 * Fields:  logo/secondLogo — uploaded refs; logoUrl — legacy string fallback.
 * Rules:   `logo.blobUrl` beats `logoUrl`; `secondLogo.blobUrl` has no legacy
 *          string fallback (there was never a `secondLogoUrl` string).
 * Sync:    local-first.
 */
export interface DepartmentBrandingLike {
  logo?: BrandingLogoRefLike;
  secondLogo?: BrandingLogoRefLike;
  logoUrl?: string;
}

/**
 * @process resolveDepartmentLogoUrl
 * Steps:           Prefer the uploaded primary-slot ref's blobUrl; else fall
 *                  back to the legacy `logoUrl` string; else undefined.
 * Inputs:          branding block (or undefined).
 * Outputs:         a logo URL/blob-name string, or undefined when none set.
 * Failure Handling:never throws; missing fields collapse to undefined.
 * Idempotency:     REQUIRED — pure function of its input.
 */
export function resolveDepartmentLogoUrl(
  branding: DepartmentBrandingLike | undefined,
): string | undefined {
  if (!branding) return undefined;
  const uploaded = branding.logo?.blobUrl;
  if (uploaded) return uploaded;
  return branding.logoUrl || undefined;
}

/**
 * @process resolveDepartmentSecondLogoUrl
 * Steps:           Resolve the secondary-slot uploaded ref's blobUrl.
 * Inputs:          branding block (or undefined).
 * Outputs:         a logo URL/blob-name string, or undefined when none set.
 * Failure Handling:never throws.
 * Idempotency:     REQUIRED — pure function of its input.
 */
export function resolveDepartmentSecondLogoUrl(
  branding: DepartmentBrandingLike | undefined,
): string | undefined {
  if (!branding) return undefined;
  return branding.secondLogo?.blobUrl || undefined;
}
